/**
 * Lazy per-routine parameter resolver for client-side signature help.
 *
 * Mirrors `insertHintColumnResolver.ts`: module-level in-memory cache keyed by
 * (connection, database, schema, routineName), 5-minute TTL, shared in-flight
 * promise for concurrent lookups.
 *
 * Used by `functionSignatureHelp.ts` when the user is typing inside a
 * `{schema}.{name}(...)` call. SQL Tools Service's textDocument/signatureHelp
 * returns `{signatures: null}` for every input in the bundled version, so the
 * only reliable signature source for MSSQL today is the database's own
 * sys.parameters catalog — which is what the `get-routine-metadata` IPC
 * exposes.
 */

import type { RoutineMetadata, RoutineMetadataResult } from '../../../../types/electron';
import type { DatabaseEngineId } from '@/core/engines';
import { getCachedPgSchema } from '@/features/connections/services/language-services/pgSchemaCache';
import { getCachedMysqlSchema } from '@/features/connections/services/language-services/mysqlSchemaCache';
import { getCachedOracleSchema } from '@/features/connections/services/language-services/oracleSchemaCache';

export type RoutineResolverParams = {
  connectionId: string;
  database: string;
  schema: string;
  name: string;
  /** Oracle-only: package name for three-level SCHEMA.PKG.PROC identifiers. */
  packageName?: string;
  /** Engine hint — enables schema-cache fast path when provided. */
  engineId?: DatabaseEngineId;
};

type CacheEntry = {
  metadata: RoutineMetadata[] | null;
  fetchedAt: number;
};

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<RoutineMetadata[] | null>>();

const TTL_MS = 5 * 60 * 1000; // 5 minutes — matches insertHintColumnResolver

/**
 * Build the cache key. Schema and name are lowercased because SQL Server
 * object lookups are case-insensitive under the default collation, and the
 * user's trigger text can use any casing (`DBO.uFnGetProductListPrice(`).
 */
export function buildCacheKey(params: RoutineResolverParams): string {
  const pkg = params.packageName ? `::${params.packageName.toLowerCase()}` : '';
  return `${params.connectionId}::${params.database}::${params.schema.toLowerCase()}${pkg}::${params.name.toLowerCase()}`;
}

function isFresh(entry: CacheEntry): boolean {
  return Date.now() - entry.fetchedAt < TTL_MS;
}

/**
 * Look up routine metadata from the engine's schema-cache snapshot.
 * Returns `undefined` if the engine has no warm snapshot or the routine isn't found.
 */
function getFromSchemaCache(params: RoutineResolverParams): RoutineMetadata[] | undefined {
  const nameLower = params.packageName
    ? `${params.packageName.toLowerCase()}.${params.name.toLowerCase()}`
    : params.name.toLowerCase();

  switch (params.engineId) {
    case 'pgsql': {
      const snapshot = getCachedPgSchema(params.connectionId);
      return snapshot?.routinesByNameLower[nameLower];
    }
    case 'mysql': {
      const snapshot = getCachedMysqlSchema(params.connectionId);
      return snapshot?.routinesByNameLower[nameLower];
    }
    case 'oracle': {
      const snapshot = getCachedOracleSchema(params.connectionId);
      return snapshot?.routinesByNameLower[nameLower];
    }
    case 'mock': {
      const snapshot = getCachedPgSchema(params.connectionId);
      return snapshot?.routinesByNameLower[nameLower];
    }
    default:
      return undefined;
  }
}

/**
 * Write routine metadata into the engine's schema-cache snapshot so it stays
 * warm for future lookups.
 */
function writeToSchemaCache(params: RoutineResolverParams, metadata: RoutineMetadata[]): void {
  const nameLower = params.packageName
    ? `${params.packageName.toLowerCase()}.${params.name.toLowerCase()}`
    : params.name.toLowerCase();

  switch (params.engineId) {
    case 'pgsql': {
      const snapshot = getCachedPgSchema(params.connectionId);
      if (snapshot) snapshot.routinesByNameLower[nameLower] = metadata;
      break;
    }
    case 'mysql': {
      const snapshot = getCachedMysqlSchema(params.connectionId);
      if (snapshot) snapshot.routinesByNameLower[nameLower] = metadata;
      break;
    }
    case 'oracle': {
      const snapshot = getCachedOracleSchema(params.connectionId);
      if (snapshot) snapshot.routinesByNameLower[nameLower] = metadata;
      break;
    }
  }
}

/**
 * Synchronously return cached routine metadata, or `undefined` if the cache is
 * cold or stale. Does NOT trigger a fetch. A cached `null` (routine does not
 * exist) is returned as `null` so callers can short-circuit without re-fetching.
 *
 * Checks the engine's schema-cache snapshot first (warm on connect), then falls
 * back to the module-level per-routine cache.
 */
export function getCachedRoutine(
  params: RoutineResolverParams,
): RoutineMetadata[] | null | undefined {
  // Fast path: check engine's schema-cache snapshot (warmed on connect)
  if (params.engineId) {
    const fromSnapshot = getFromSchemaCache(params);
    if (fromSnapshot !== undefined) return fromSnapshot;
  }

  // Module-level per-routine cache
  const key = buildCacheKey(params);
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (!isFresh(entry)) {
    cache.delete(key);
    return undefined;
  }
  return entry.metadata;
}

/**
 * Fetch routine metadata via the `get-routine-metadata` IPC, caching the
 * result (including negative hits — a non-existent routine is cached as
 * `null`). Concurrent calls for the same cache key share one round trip.
 *
 * On success, writes to BOTH the module-level cache and the engine's
 * schema-cache snapshot (if available).
 */
export async function fetchRoutine(
  params: RoutineResolverParams,
): Promise<RoutineMetadata[] | null> {
  const key = buildCacheKey(params);

  const cached = cache.get(key);
  if (cached && isFresh(cached)) {
    return cached.metadata;
  }

  const existing = inFlight.get(key);
  if (existing) return existing;

  const promise = (async (): Promise<RoutineMetadata[] | null> => {
    try {
      const api = (globalThis as {
        window?: {
          electronAPI?: {
            getRoutineMetadata?: (r: RoutineResolverParams) => Promise<RoutineMetadataResult>;
          };
        };
      }).window?.electronAPI;
      if (!api?.getRoutineMetadata) {
        return null;
      }

      const result = await api.getRoutineMetadata({
        connectionId: params.connectionId,
        database: params.database,
        schema: params.schema,
        name: params.name,
        ...(params.packageName ? { packageName: params.packageName } : {}),
      });

      // `success: true, metadata: null` means "routine doesn't exist" — that's
      // a valid negative cache entry, not an error.
      if (!result?.success) {
        return null;
      }

      const metadata = result.metadata ?? null;
      cache.set(key, { metadata, fetchedAt: Date.now() });

      // Write-back to schema cache so the snapshot stays warm
      if (metadata && params.engineId) {
        writeToSchemaCache(params, metadata);
      }

      return metadata;
    } catch {
      // Signature help is best-effort UX. Swallow IPC errors and let the
      // provider return null.
      return null;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

/**
 * Clear cached routines for a given connection. Call on disconnect or when
 * the schema is known to have changed (e.g. CREATE/ALTER PROCEDURE).
 */
export function invalidateRoutines(connectionId: string): void {
  const prefix = `${connectionId}::`;
  for (const key of Array.from(cache.keys())) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}

/**
 * Test-only: inject routine metadata directly into the cache, bypassing IPC.
 * Mirrors `injectPgSchemaForTest` in pgSchemaCache.ts.
 * Accepts a single RoutineMetadata (wrapped into a one-element array) or an
 * array of overloads.
 */
export function injectRoutineForTest(
  params: RoutineResolverParams,
  metadata: RoutineMetadata | RoutineMetadata[] | null,
): void {
  const key = buildCacheKey(params);
  const normalized = metadata === null ? null : (Array.isArray(metadata) ? metadata : [metadata]);
  cache.set(key, { metadata: normalized, fetchedAt: Date.now() });
}

/**
 * Test-only: clear all caches and in-flight promises.
 */
export function __resetRoutineResolverForTest(): void {
  cache.clear();
  inFlight.clear();
}
